import { memo, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import { Button, Input, Result, Typography } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { ThreeDot } from 'react-loading-indicators'
import { EnhancementCharacteristicsResponse, ICharacteristic } from 'models/product'
import { productApi } from 'api/product'
import { useSocketRequest } from 'shared/hooks/useSocketRequest'
import { AiBadge, OrangeButton } from 'components/ui'
import styles from './AiProductCharacteristics.module.scss'

interface AiProductCharacteristicsProps {
  productId: string
  characteristics: ICharacteristic[]
  setCharacteristics: React.Dispatch<React.SetStateAction<ICharacteristic[]>>
  setItemId: (id: string) => void
  type: 'create' | 'edit'
}

export const AiProductCharacteristics: React.FC<AiProductCharacteristicsProps> = memo(({ productId, type, characteristics, setCharacteristics, setItemId }) => {

  const handleCharacteristicChange = useCallback((index: number, field: 'name' | 'value', newValue: string) => {
    setCharacteristics(prev => prev.map((char, i) =>
      i === index ? { ...char, [field]: newValue } : char
    ))
  }, [])

  const handleRemoveCharacteristic = useCallback((index: number) => {
    setCharacteristics(prev => prev.filter((_, i) => i !== index))
  }, [])

  const createJob = useCallback(async () => {
    const { data } = await productApi.createAiCharacteristics({
      productId
    })
    return { jobId: data.jobId }
  }, [productId])

  const { isLoaded, isError, data, retry } = useSocketRequest<EnhancementCharacteristicsResponse>({
    enabled: type === 'create',
    createJob
  })

  useEffect(() => {
    if (data) {
      setCharacteristics(prev => [...prev, ...data.characteristics])
      setItemId(data.enhancementId)
    }
  }, [data])

  return (
    <div className={styles.container}>
      <Typography.Title level={4} className={styles.title}>Характеристики:</Typography.Title>
      <div className={classNames(styles.contentWrapper, { [styles.contentWrapperWithData]: characteristics.length > 0 })}>
        {!isLoaded && (
          <ThreeDot color='#FF7905' size='medium' text='' textColor='' />
        )}
        {isError && isLoaded && (
          <Result
            status='error'
            title='Ошибка при генерации характеристик'
            subTitle='Попробуйте еще раз'
            extra={[
              <OrangeButton onClick={retry}>
                Попробовать еще раз
              </OrangeButton>
            ]}
          />
        )}
        {!isError && isLoaded && (
          <>
            <Typography.Text className={styles.description}>
              Будьте внимательны, характеристики сгенерированы автоматически, исходя из фото и описания. Вы можете их редактировать.
            </Typography.Text>
            <div className={styles.characteristics}>
              {characteristics.map((characteristic, index) => (
                <div key={index} className={styles.characteristicRow}>
                  <div className={styles.inputGroup}>
                    <div className={styles.inputWrapper}>
                      <Input
                        value={characteristic.name}
                        onChange={(e) => handleCharacteristicChange(index, 'name', e.target.value)}
                        placeholder='Название характеристики'
                        size='large'
                        className={styles.characteristicInput}
                      />
                      <AiBadge />
                    </div>
                    <span className={styles.separator}>:</span>
                    <div className={styles.inputWrapper}>
                      <Input
                        value={characteristic.value}
                        onChange={(e) => handleCharacteristicChange(index, 'value', e.target.value)}
                        placeholder='Значение'
                        size='large'
                        className={styles.characteristicInput}
                      />
                      <AiBadge />
                    </div>
                  </div>
                  <Button
                    type='primary'
                    icon={<CloseOutlined />}
                    size='small'
                    onClick={() => handleRemoveCharacteristic(index)}
                    className={styles.deleteButton}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
})